import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useContext } from 'react';
import { baseApiUrl, fetcher, ServerErrorResponse } from 'data_sources/constants';
import { AppContext } from 'views/App/context';
import type {
  LexipolComplianceItem,
  LexipolComplianceItemCreateResponse,
  CreateLexipolComplianceItemRequest,
  UpdateLexipolComplianceItemRequest,
  ReorderLexipolComplianceItemsRequest,
} from '../../_types/awb';

const basePath = (
  accreditationBodyId: string,
  versionId: string,
  standardId: string
) =>
  `${baseApiUrl()}/awb/accreditation-body/v1/accreditation-bodies/${accreditationBodyId}/versions/${versionId}/standards/${standardId}/compliance-items`;

export async function getComplianceItemsLexipol(
  accreditationBodyId: string,
  versionId: string,
  standardId: string
): Promise<LexipolComplianceItem[]> {
  const url = basePath(accreditationBodyId, versionId, standardId);
  const data = await fetcher({ method: 'get', url });
  return Array.isArray(data) ? data : [];
}

export async function createComplianceItemLexipol(
  accreditationBodyId: string,
  versionId: string,
  standardId: string,
  body: CreateLexipolComplianceItemRequest
): Promise<LexipolComplianceItemCreateResponse> {
  const url = basePath(accreditationBodyId, versionId, standardId);
  return fetcher({ method: 'post', url, body });
}

export async function updateComplianceItemLexipol(
  accreditationBodyId: string,
  versionId: string,
  standardId: string,
  complianceItemId: string,
  body: UpdateLexipolComplianceItemRequest
): Promise<LexipolComplianceItem> {
  const url = `${basePath(accreditationBodyId, versionId, standardId)}/${complianceItemId}`;
  return fetcher({ method: 'patch', url, body });
}

export async function deleteComplianceItemLexipol(
  accreditationBodyId: string,
  versionId: string,
  standardId: string,
  complianceItemId: string
): Promise<void> {
  const url = `${basePath(accreditationBodyId, versionId, standardId)}/${complianceItemId}`;
  await fetcher({ method: 'delete', url });
}

export async function reorderComplianceItemsLexipol(
  accreditationBodyId: string,
  versionId: string,
  standardId: string,
  body: ReorderLexipolComplianceItemsRequest
): Promise<LexipolComplianceItem[]> {
  const url = `${basePath(accreditationBodyId, versionId, standardId)}/order`;
  const data = await fetcher({ method: 'patch', url, body });
  return Array.isArray(data) ? data : [];
}

export interface UseComplianceItemsLexipolReturn {
  data: LexipolComplianceItem[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
  createItem: (vars: CreateLexipolComplianceItemRequest) => void;
  createItemAsync: (vars: CreateLexipolComplianceItemRequest) => Promise<LexipolComplianceItemCreateResponse>;
  isCreatingItem: boolean;
  updateItem: (vars: {
    complianceItemId: string;
    updates: UpdateLexipolComplianceItemRequest;
  }) => void;
  updateItemAsync: (vars: {
    complianceItemId: string;
    updates: UpdateLexipolComplianceItemRequest;
  }) => Promise<LexipolComplianceItem>;
  isUpdatingItem: boolean;
  deleteItem: (vars: { complianceItemId: string }) => void;
  deleteItemAsync: (vars: { complianceItemId: string }) => Promise<void>;
  isDeletingItem: boolean;
  reorder: (vars: ReorderLexipolComplianceItemsRequest) => void;
  reorderAsync: (vars: ReorderLexipolComplianceItemsRequest) => Promise<LexipolComplianceItem[]>;
  isReordering: boolean;
}

/**
 * Lexipol compliance items (template management). Real API only.
 * Requires awbPremiumLexipol feature flag. Used in AccreditationBodies.
 */
export default function useComplianceItemsLexipol(
  accreditationBodyId: string,
  versionId: string,
  standardId: string,
  options = {},
  onError?: (errors: ServerErrorResponse) => void
): UseComplianceItemsLexipolReturn {
  const { isFeatureEnabled } = useContext(AppContext);
  const queryClient = useQueryClient();
  const isEnabled = isFeatureEnabled('awbPremiumLexipol');

  const queryKey = [
    'compliance_items_lexipol',
    accreditationBodyId,
    versionId,
    standardId,
  ] as const;

  const query = useQuery<LexipolComplianceItem[], Error>(
    queryKey,
    () => getComplianceItemsLexipol(accreditationBodyId, versionId, standardId),
    {
      enabled: isEnabled && !!accreditationBodyId && !!versionId && !!standardId,
      staleTime: 900000,
      ...options,
    }
  );

  const invalidate = () => {
    queryClient.invalidateQueries(queryKey);
  };

  const createMutation = useMutation<
    LexipolComplianceItemCreateResponse,
    ServerErrorResponse,
    CreateLexipolComplianceItemRequest
  >(
    (body) =>
      createComplianceItemLexipol(
        accreditationBodyId,
        versionId,
        standardId,
        body
      ),
    { onSuccess: invalidate, onError }
  );

  const updateMutation = useMutation<
    LexipolComplianceItem,
    ServerErrorResponse,
    { complianceItemId: string; updates: UpdateLexipolComplianceItemRequest }
  >(
    ({ complianceItemId, updates }) =>
      updateComplianceItemLexipol(
        accreditationBodyId,
        versionId,
        standardId,
        complianceItemId,
        updates
      ),
    { onSuccess: invalidate, onError }
  );

  const deleteMutation = useMutation<
    void,
    ServerErrorResponse,
    { complianceItemId: string }
  >(
    ({ complianceItemId }) =>
      deleteComplianceItemLexipol(
        accreditationBodyId,
        versionId,
        standardId,
        complianceItemId
      ),
    { onSuccess: invalidate, onError }
  );

  const reorderMutation = useMutation<
    LexipolComplianceItem[],
    ServerErrorResponse,
    ReorderLexipolComplianceItemsRequest
  >(
    (body) =>
      reorderComplianceItemsLexipol(
        accreditationBodyId,
        versionId,
        standardId,
        body
      ),
    { onSuccess: invalidate, onError }
  );

  const noop = () => {};
  const reject = () => Promise.reject(new Error('Feature disabled'));

  if (!isEnabled) {
    return {
      data: [],
      isLoading: false,
      isError: false,
      error: null,
      refetch: () => Promise.resolve({}),
      createItem: noop,
      createItemAsync: (() => Promise.reject(new Error('Feature disabled'))) as UseComplianceItemsLexipolReturn['createItemAsync'],
      isCreatingItem: false,
      updateItem: noop,
      updateItemAsync: reject as UseComplianceItemsLexipolReturn['updateItemAsync'],
      isUpdatingItem: false,
      deleteItem: noop,
      deleteItemAsync: reject as UseComplianceItemsLexipolReturn['deleteItemAsync'],
      isDeletingItem: false,
      reorder: noop,
      reorderAsync: reject as UseComplianceItemsLexipolReturn['reorderAsync'],
      isReordering: false,
    };
  }

  return {
    ...query,
    data: query.data ?? [],
    createItem: createMutation.mutate,
    createItemAsync: createMutation.mutateAsync,
    isCreatingItem: createMutation.isLoading,
    updateItem: updateMutation.mutate,
    updateItemAsync: updateMutation.mutateAsync,
    isUpdatingItem: updateMutation.isLoading,
    deleteItem: deleteMutation.mutate,
    deleteItemAsync: deleteMutation.mutateAsync,
    isDeletingItem: deleteMutation.isLoading,
    reorder: reorderMutation.mutate,
    reorderAsync: reorderMutation.mutateAsync,
    isReordering: reorderMutation.isLoading,
  };
}
